import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Building2, FileText, ClipboardCheck, Phone, Users, 
  ArrowRight, LogOut, Plus, Clock, CheckCircle, AlertCircle,
  Activity, TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function FacilityDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [facility, setFacility] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    submitted: 0,
    under_review: 0,
    accepted: 0
  });

  useEffect(() => {
    const token = localStorage.getItem("facility_token");
    const facilityData = localStorage.getItem("facility_data");
    
    if (!token || !facilityData) {
      navigate("/facility");
      return;
    }
    
    setFacility(JSON.parse(facilityData));
    loadReferrals(token);
  }, [navigate]);

  const loadReferrals = async (token) => {
    try {
      const response = await api.get("/facility/referrals", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const refs = response.data.referrals || [];
      setReferrals(refs);
      
      // Calculate stats
      setStats({
        total: refs.length,
        submitted: refs.filter(r => r.status === "submitted").length,
        under_review: refs.filter(r => r.status === "under_review").length,
        accepted: refs.filter(r => r.status === "accepted").length
      });
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("facility_token");
        localStorage.removeItem("facility_data");
        navigate("/facility");
      } else {
        toast.error("Failed to load referrals");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("facility_token");
    localStorage.removeItem("facility_data");
    toast.success("Logged out successfully");
    navigate("/facility");
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: "bg-blue-100 text-blue-800",
      under_review: "bg-yellow-100 text-yellow-800",
      accepted: "bg-green-100 text-green-800",
      declined: "bg-red-100 text-red-800"
    };
    const labels = {
      submitted: t("facility.status.submitted"),
      under_review: t("facility.status.underReview"),
      accepted: t("facility.status.accepted"),
      declined: t("facility.status.declined")
    };
    return (
      <Badge className={styles[status] || "bg-gray-100"}>
        {labels[status] || status}
      </Badge>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      routine: "bg-gray-100 text-gray-800",
      urgent: "bg-orange-100 text-orange-800",
      emergent: "bg-red-100 text-red-800"
    };
    return (
      <Badge variant="outline" className={styles[urgency] || ""}>
        {urgency?.toUpperCase()}
      </Badge>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
        
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t("facility.dashboard.loading")}</p>
        </div>
        
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Building2 className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold">{facility?.facility_name}</h1>
              <Badge variant="secondary">{facility?.facility_type?.replace("_", " ").toUpperCase()}</Badge>
            </div>
            <p className="text-muted-foreground">
              {t("facility.dashboard.welcome")}, {facility?.contact_name}
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" /> {t("facility.dashboard.logout")}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("facility.dashboard.totalReferrals")}</p>
                  <p className="text-3xl font-bold">{stats.total}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("facility.dashboard.pendingReview")}</p>
                  <p className="text-3xl font-bold">{stats.submitted + stats.under_review}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("facility.dashboard.accepted")}</p>
                  <p className="text-3xl font-bold">{stats.accepted}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("facility.dashboard.thisMonth")}</p>
                  <p className="text-3xl font-bold">
                    {referrals.filter(r => {
                      const date = new Date(r.created_at);
                      const now = new Date();
                      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                    }).length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/facility/referral/new")}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Plus className="w-7 h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{t("facility.dashboard.newReferral")}</h3>
                  <p className="text-sm text-muted-foreground">{t("facility.dashboard.newReferralDesc")}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate("/facility/criteria")}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-green-100 flex items-center justify-center">
                  <ClipboardCheck className="w-7 h-7 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{t("facility.dashboard.criteriaTool")}</h3>
                  <p className="text-sm text-muted-foreground">{t("facility.dashboard.criteriaDesc")}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => window.open("tel:703-578-7108")}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Phone className="w-7 h-7 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{t("facility.dashboard.callReferral")}</h3>
                  <p className="text-sm text-muted-foreground">{t("facility.dashboard.callReferralDesc")}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-muted-foreground ml-auto" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Referrals */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>{t("facility.dashboard.recentReferrals")}</CardTitle>
                <CardDescription>{t("facility.dashboard.recentReferralsDesc")}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate("/facility/referrals")}>
                {t("facility.dashboard.viewAll")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {referrals.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">{t("facility.dashboard.noReferrals")}</h3>
                <p className="text-muted-foreground mb-4">
                  {t("facility.dashboard.noReferralsDesc")}
                </p>
                <Button onClick={() => navigate("/facility/referral/new")}>
                  <Plus className="w-4 h-4 mr-2" /> {t("facility.dashboard.newReferralBtn")}
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-3 font-medium">{t("facility.referrals.patient")}</th>
                      <th className="pb-3 font-medium">{t("facility.referrals.diagnosis")}</th>
                      <th className="pb-3 font-medium">{t("facility.referrals.urgency")}</th>
                      <th className="pb-3 font-medium">{t("facility.referrals.status")}</th>
                      <th className="pb-3 font-medium">{t("facility.referrals.date")}</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.slice(0, 5).map((ref) => (
                      <tr key={ref.id} className="border-b last:border-0">
                        <td className="py-4">
                          <div className="font-medium">
                            {ref.patient?.first_name} {ref.patient?.last_name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            DOB: {ref.patient?.dob}
                          </div>
                        </td>
                        <td className="py-4">
                          <div className="max-w-[200px] truncate">
                            {ref.clinical?.primary_diagnosis}
                          </div>
                        </td>
                        <td className="py-4">
                          {getUrgencyBadge(ref.urgency)}
                        </td>
                        <td className="py-4">
                          {getStatusBadge(ref.status)}
                        </td>
                        <td className="py-4 text-sm text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-4">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/facility/referral/${ref.id}`)}
                          >
                            {t("facility.referrals.view")}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      
    </div>
  );
}
